import { useState } from "react";
import { supabase } from "./supabase.js";
import { Button, Modal } from "./ui.js";
import { formatDateTimeMedium, formatWeekday } from "./datetime.js";

/** "Mark completed" is offered on a confirmed booking whose visit has started -- the same rule the database enforces. */
export function canMarkCompleted(status: string, startsAt: string | null | undefined): boolean {
  return status === "confirmed" && !!startsAt && new Date(startsAt) <= new Date();
}

/** The total of a booking's service prices. totalCents is null when none of its services has a price. */
export function bookingTotal(prices: Array<number | null | undefined>): { totalCents: number | null; unpricedCount: number } {
  const priced = prices.filter((p): p is number => typeof p === "number");
  return { totalCents: priced.length > 0 ? priced.reduce((a, b) => a + b, 0) : null, unpricedCount: prices.length - priced.length };
}

function formatMoney(cents: number): string {
  return new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(cents / 100);
}

/** The total, larger and bolder than the text around it, so the amount to collect is the first thing seen. */
function TotalLine({ totalCents, unpricedCount }: { totalCents: number | null; unpricedCount: number }) {
  if (totalCents === null) return null;
  return (
    <div style={{ margin: "14px 0 4px", paddingTop: 12, borderTop: "1px solid var(--rc-border)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: "var(--rc-text)" }}>Total</span>
        <span style={{ fontSize: 24, fontWeight: 700, color: "var(--rc-text)", fontVariantNumeric: "tabular-nums" }}>{formatMoney(totalCents)}</span>
      </div>
      {unpricedCount > 0 && (
        <div style={{ fontSize: 12, color: "var(--rc-text-muted)", marginTop: 2 }}>
          Not including {unpricedCount} service{unpricedCount === 1 ? "" : "s"} with no price set.
        </div>
      )}
    </div>
  );
}

function completeErrorMessage(message: string): string {
  if (message.includes("booking_not_started")) return "That visit hasn't started yet, so it can't be marked completed.";
  if (message.includes("booking_not_found")) return "That booking is no longer confirmed, so it can't be changed. Refresh the list.";
  return message;
}

/**
 * The "Mark completed" confirmation, shared by the Bookings list and the Dashboard so both behave the same: it asks first,
 * calls rpc_portal_complete_booking, and stays open with the reason if the database refuses.
 */
export function CompleteBookingModal({
  bookingId,
  customer,
  service,
  startsAt,
  totalCents,
  unpricedCount = 0,
  onClose,
  onDone,
}: {
  bookingId: string;
  customer: string | null;
  service: string;
  startsAt: string | null;
  /** Sum of the booking's service prices; null when none has a price (the total is then left out). */
  totalCents: number | null;
  unpricedCount?: number;
  onClose: () => void;
  /** Called after the booking is completed (the dialog has asked to close; refresh your list here). */
  onDone: () => void | Promise<void>;
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function confirm() {
    setSaving(true);
    setError(null);
    const { error: err } = await supabase.rpc("rpc_portal_complete_booking", { p_booking_id: bookingId });
    setSaving(false);
    if (err) {
      setError(completeErrorMessage(err.message ?? ""));
      return;
    }
    onClose();
    await onDone();
  }

  return (
    <Modal title="Mark completed" onClose={onClose} width={400}>
      <p style={{ fontSize: 13.5, color: "var(--rc-text)", marginTop: 0 }}>
        Mark {customer ?? "this customer"}'s {service}
        {startsAt ? ` on ${formatWeekday(startsAt)}, ${formatDateTimeMedium(startsAt)}` : ""} as completed?
      </p>
      <TotalLine totalCents={totalCents} unpricedCount={unpricedCount} />
      <p style={{ fontSize: 12.5, color: "var(--rc-text-muted)" }}>
        It stays in the list as Completed. A completed booking can no longer be rescheduled, added to or cancelled.
      </p>
      {error && <p style={{ color: "var(--rc-danger)", fontSize: 13, margin: "0 0 8px" }}>{error}</p>}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
        <Button variant="secondary" onClick={onClose} disabled={saving}>
          Not yet
        </Button>
        <Button variant="primary" onClick={() => void confirm()} disabled={saving}>
          {saving ? "Saving..." : "Mark completed"}
        </Button>
      </div>
    </Modal>
  );
}

export interface CompleteItem {
  id: string;
  customer: string | null;
  service: string;
  startsAt: string | null;
  totalCents: number | null;
  unpricedCount: number;
}

/**
 * "Mark completed" for several bookings at once. It asks once, then completes each (the same checked function as one at a
 * time, so every booking is still checked on its own). If some fail, the dialog stays open and says which and why; the
 * ones that worked stay completed.
 */
export function CompleteBookingsModal({
  items,
  onClose,
  onDone,
}: {
  items: CompleteItem[];
  onClose: () => void;
  onDone: () => void | Promise<void>;
}) {
  const [saving, setSaving] = useState(false);
  const [failures, setFailures] = useState<Array<{ item: CompleteItem; reason: string }> | null>(null);

  async function confirm() {
    setSaving(true);
    const results = await Promise.all(
      items.map(async (item) => {
        const { error } = await supabase.rpc("rpc_portal_complete_booking", { p_booking_id: item.id });
        return { item, reason: error ? completeErrorMessage(error.message ?? "") : null };
      })
    );
    setSaving(false);
    const failed = results.filter((r): r is { item: CompleteItem; reason: string } => r.reason !== null);
    await onDone(); // refresh either way: some may have worked
    if (failed.length === 0) {
      onClose();
      return;
    }
    setFailures(failed);
  }

  const done = failures ? items.length - failures.length : 0;
  return (
    <Modal title={`Mark ${items.length} bookings completed`} onClose={onClose} width={460}>
      {failures ? (
        <>
          <p style={{ fontSize: 13.5, color: "var(--rc-text)", marginTop: 0 }}>
            {done} of {items.length} marked completed. {failures.length} could not be:
          </p>
          <ul style={{ margin: "0 0 12px", paddingLeft: 18, fontSize: 13, color: "var(--rc-danger)" }}>
            {failures.map((f) => (
              <li key={f.item.id}>
                {f.item.customer ?? "Customer"}, {f.item.service}: {f.reason}
              </li>
            ))}
          </ul>
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
            <Button variant="primary" onClick={onClose}>
              Close
            </Button>
          </div>
        </>
      ) : (
        <>
          <p style={{ fontSize: 13.5, color: "var(--rc-text)", marginTop: 0 }}>Mark these {items.length} bookings as completed?</p>
          <ul style={{ margin: "0 0 8px", paddingLeft: 18, fontSize: 13, color: "var(--rc-text)", maxHeight: 180, overflowY: "auto" }}>
            {items.map((i) => (
              <li key={i.id}>
                {i.customer ?? "Customer"}, {i.service}
                {i.startsAt ? ` (${formatWeekday(i.startsAt)}, ${formatDateTimeMedium(i.startsAt)})` : ""}
              </li>
            ))}
          </ul>
          <TotalLine
            totalCents={items.some((i) => i.totalCents !== null) ? items.reduce((sum, i) => sum + (i.totalCents ?? 0), 0) : null}
            unpricedCount={items.reduce((sum, i) => sum + i.unpricedCount, 0)}
          />
          <p style={{ fontSize: 12.5, color: "var(--rc-text-muted)" }}>
            They stay in the list as Completed. A completed booking can no longer be rescheduled, added to or cancelled.
          </p>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
            <Button variant="secondary" onClick={onClose} disabled={saving}>
              Not yet
            </Button>
            <Button variant="primary" onClick={() => void confirm()} disabled={saving}>
              {saving ? "Saving..." : `Mark ${items.length} completed`}
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}
